import { useState } from 'react'
import { useRevogarFato } from '../../../api/queries/ordensDeServico'
import { Botao } from '../../../components/Botao'
import { AvisoDeErro } from '../../../components/Estados'
import { Cartao } from '../../../components/ListaDeDados'
import { dataHora, dataSimples } from '../../../lib/datas'
import type { DetalheDaOs, FatoDaOs } from '../../../types/ordemDeServico'
// O diálogo de texto obrigatório já existe e é genérico: reabrir competência, dispensar sanção e
// retirar um registro são o mesmo ato — só valem com a justificativa escrita, e ela vai para a
// trilha. Uma terceira cópia divergiria justamente na validação do vazio, que é o ponto todo.
import { DialogoDeTexto } from '../../chamados/fiscalizacao/DialogoDeTexto'
import { OS } from '../textosDaOs'
import { RegistrarFato } from './RegistrarFato'

/**
 * A linha do tempo da ordem: cada ato datado, com o documento que prova a data.
 *
 * **É desta lista que os prazos saem.** Sem `ENTREGA_AO_PREPOSTO` registrada, nenhum prazo da ordem
 * é apurável (6.24.6, p.45) — por isso o vazio aqui não é "nada a mostrar", e sim a explicação de
 * que ninguém informou o que aconteceu depois da emissão.
 *
 * **Cada fato mostra de que lado do contrato ele é.** O termo de recebimento provisório é ato da
 * Prefeitura; a entrega dos produtos é da contratada. Sem isso, uma lista de datas atrasadas seria
 * lida como falta de um lado só — e o lado errado.
 *
 * @param props a ordem carregada e se quem está vendo pode escrever
 * @returns o cartão do andamento
 */
export function AndamentoDaOs({ ordem, podeEscrever }: { ordem: DetalheDaOs; podeEscrever: boolean }) {
  const [registrando, definirRegistrando] = useState(false)
  const [retirando, definirRetirando] = useState<FatoDaOs | null>(null)
  const revogar = useRevogarFato()

  return (
    <Cartao
      titulo={OS.andamento.titulo}
      descricao={OS.andamento.ajuda}
      acao={
        podeEscrever &&
        !ordem.revogacao && (
          <Botao tamanho="pequeno" onClick={() => definirRegistrando(true)}>
            {OS.andamento.registrar}
          </Botao>
        )
      }
    >
      {revogar.isError && <AvisoDeErro erro={revogar.error} />}

      {ordem.fatos.length === 0 ? (
        <p className="max-w-prose text-sm text-slate-600">{OS.andamento.vazio}</p>
      ) : (
        <ol className="space-y-3">
          {ordem.fatos.map((fato) => (
            <li key={fato.id} className="rounded-lg border border-slate-200 p-3">
              <Fato fato={fato} podeEscrever={podeEscrever} aoRetirar={() => definirRetirando(fato)} />
            </li>
          ))}
        </ol>
      )}

      {registrando && <RegistrarFato ordem={ordem} aoFechar={() => definirRegistrando(false)} />}

      {retirando && (
        <DialogoDeTexto
          titulo={OS.andamento.revogarFato}
          ajuda={OS.andamento.revogarFatoAjuda}
          rotulo="Por que este registro está sendo retirado"
          aoConfirmar={(motivo) => {
            revogar.mutate({ id: ordem.id, fatoId: retirando.id, motivo })
            definirRetirando(null)
          }}
          aoFechar={() => definirRetirando(null)}
        />
      )}
    </Cartao>
  )
}

/**
 * Um ato na linha do tempo.
 *
 * A data do ATO usa `dataSimples` (ela é `AAAA-MM-DD` e é dela que o contrato conta prazo); a data
 * do REGISTRO usa `dataHora`, porque é instante e a hora importa para a trilha. Trocar as duas
 * funções de lugar produz um dia a menos na que vai para o processo administrativo.
 */
function Fato({
  fato,
  podeEscrever,
  aoRetirar,
}: {
  fato: FatoDaOs
  podeEscrever: boolean
  aoRetirar: () => void
}) {
  return (
    <>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-slate-900">{fato.rotulo}</h3>
          <p className="text-xs text-slate-500">
            {OS.andamento.responsavelDoAto(OS.lados[fato.responsavel])} · {fato.fonteDoTr}
          </p>
        </div>
        <time dateTime={fato.ocorridoEm} className="whitespace-nowrap text-sm font-medium tabular-nums text-slate-800">
          {dataSimples(fato.ocorridoEm)}
        </time>
      </div>

      <dl className="mt-2 grid grid-cols-[auto,1fr] gap-x-3 gap-y-1 text-sm">
        {fato.meio && (
          <>
            <dt className="text-slate-500">{OS.andamento.meio}</dt>
            <dd className="min-w-0 text-slate-800">{OS.meios[fato.meio]}</dd>
          </>
        )}
        {fato.referencia && (
          <>
            <dt className="text-slate-500">{OS.andamento.referencia}</dt>
            <dd className="min-w-0 text-slate-800">{fato.referencia}</dd>
          </>
        )}
      </dl>

      {fato.observacao && <p className="mt-2 whitespace-pre-line text-sm text-slate-700">{fato.observacao}</p>}

      <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-slate-500">
          {OS.registradaPor(fato.registradoPorNome, dataHora(fato.registradoEm))}
        </p>
        {podeEscrever && (
          <Botao tamanho="pequeno" aparencia="perigo" onClick={aoRetirar}>
            {OS.andamento.revogarFato}
          </Botao>
        )}
      </div>
    </>
  )
}
